import {useCallback, useEffect, useMemo, useState} from "react";
import {
    createPermission,
    deletePermission,
    listFuncionalidadesReference,
    listLicencasReference,
    listModulosReference,
    listPlanosReference,
    listPermissions,
    listRecursosReference,
    listSistemasReference,
    updatePermission
} from "./PermissionsService";
import {
    buildPermissionTree,
    extractListPayload,
    getBackendMessage,
    getPlanModuloIds,
    normalizePermissionRows,
    PERMISSION_ACTION_OPTIONS
} from "./permissionsUtils";

const createEmptyForm = () => ({
    id_licenca: '',
    id_sistema: '',
    nome: '',
    roleName: '',
    acoes: ['VISUALIZAR'],
    permissions: [],
    usuarios_vinculados: '',
    descricao: '',
    description: '',
    ultima_revisao: '',
});

const normalizePermissions = (row) => {
    if (Array.isArray(row?.permissions) && row.permissions.length) {
        return row.permissions.map((item, index) => {
            const resourceId = item?.resourceId || item?.resource?.id || item?.id;
            return {
                ...item,
                id: item?.id || item?.permissionId || `perm-${resourceId || index}-${index}`,
                nome: item?.nome || item?.resourceName || item?.resource?.resourceName || '',
                resource: {
                    ...item?.resource,
                    id: resourceId || '',
                    resourceName: item?.resourceName || item?.nome || item?.resource?.resourceName || '-',
                    endpoint: item?.endpoint || item?.resource?.endpoint || '-',
                    httpMethod: item?.httpMethod || item?.resource?.httpMethod || item?.resource?.metodo || 'GET',
                },
            };
        });
    }
    if (Array.isArray(row?.acoes) && row.acoes.length) {
        return row.acoes.map((item, index) => ({
            id: `perm-action-${index}`,
            nome: item,
            allowed: true,
            resource: {
                id: `action-${item}-${index}`,
                resourceName: item,
                endpoint: '-',
                httpMethod: 'GET',
            },
        }));
    }
    return PERMISSION_ACTION_OPTIONS.map((item, index) => ({
        id: `perm-${index}`,
        nome: item,
        allowed: item === 'VISUALIZAR',
        resource: {
            id: `resource-${item}`,
            resourceName: item,
            endpoint: '-',
            httpMethod: 'GET',
        },
    }));
};

const normalizeToForm = (row) => ({
    ...createEmptyForm(),
    ...row,
    id_licenca: row?.id_licenca || row?.licenseId || row?.licenca?.id || '',
    id_sistema: row?.id_sistema || row?.sistema?.id || '',
    nome: row?.nome || row?.roleName || '',
    roleName: row?.roleName || row?.nome || '',
    descricao: row?.descricao || row?.description || '',
    description: row?.description || row?.descricao || '',
    permissions: normalizePermissions(row),
    acoes: (normalizePermissions(row) || []).filter((item) => item.allowed).map((item) => item.nome || item.resource?.resourceName).filter(Boolean),
    usuarios_vinculados: row?.usuarios_vinculados ?? '',
});

const toPayload = (form) => ({
    roleName: form.roleName?.trim() || form.nome?.trim() || '',
    description: form.description?.trim() || form.descricao?.trim() || '',
    licenseId: form.id_licenca,
    permissions: (form.permissions || [])
        .map((item) => ({
            resourceId: item?.resourceId || item?.resource?.id || '',
            allowed: Boolean(item?.allowed),
        }))
        .filter((item) => item.resourceId),
});

export const usePermissions = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({error: '', success: ''});
    const [filter, setFilter] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailsTarget, setDetailsTarget] = useState(null);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [form, setForm] = useState(createEmptyForm());
    const [references, setReferences] = useState({licencas: [], planos: [], sistemas: [], modulos: [], funcionalidades: [], recursos: []});
    const [selectedLicencaId, setSelectedLicencaId] = useState('');

    const loadReferences = useCallback(async () => {
        try {
            const [licencasResponse, planosResponse, sistemasResponse, modulosResponse, funcionalidadesResponse, recursosResponse] = await Promise.all([
                listLicencasReference(),
                listPlanosReference(),
                listSistemasReference(),
                listModulosReference(),
                listFuncionalidadesReference(),
                listRecursosReference(),
            ]);
            setReferences({
                licencas: extractListPayload(licencasResponse).items,
                planos: extractListPayload(planosResponse).items,
                sistemas: extractListPayload(sistemasResponse).items,
                modulos: extractListPayload(modulosResponse).items,
                funcionalidades: extractListPayload(funcionalidadesResponse).items,
                recursos: extractListPayload(recursosResponse).items,
            });
            setSelectedLicencaId((current) => current || extractListPayload(licencasResponse).items[0]?.id || '');
        } catch {
            // silencioso
        }
    }, []);

    const loadPermissions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listPermissions();
            setRows(normalizePermissionRows(extractListPayload(response).items));
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível carregar políticas de acesso.'), success: ''});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadReferences();
    }, [loadReferences]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    const rowsWithReferences = useMemo(() => rows.map((row) => ({
        ...row,
        licenca: references.licencas.find((item) => item.id === row.id_licenca) || row.licenca,
        sistema: references.sistemas.find((item) => item.id === row.id_sistema) || row.sistema,
    })), [references.licencas, references.sistemas, rows]);

    const selectedLicenca = useMemo(() => (
        references.licencas.find((item) => item.id === selectedLicencaId)
        || rowsWithReferences.find((item) => item.id_licenca === selectedLicencaId)?.licenca
        || references.licencas[0]
        || rowsWithReferences[0]?.licenca
        || null
    ), [references.licencas, rowsWithReferences, selectedLicencaId]);

    const selectedLicencaWithPlan = useMemo(() => {
        if (!selectedLicenca) return null;
        const plan = selectedLicenca.plano || selectedLicenca.plan || null;
        const planId = selectedLicenca.id_plano || selectedLicenca.planId || plan?.id || '';
        const planReference = references.planos.find((item) => item.id === planId);

        return {
            ...selectedLicenca,
            plano: planReference ? {...plan, ...planReference} : plan,
            plan: planReference ? {...plan, ...planReference} : plan,
        };
    }, [references.planos, selectedLicenca]);

    const recursosDisponiveis = useMemo(() => {
        const allowedModuloIds = getPlanModuloIds(selectedLicencaWithPlan?.plano || selectedLicencaWithPlan?.plan);
        return buildPermissionTree(references, {allowedModuloIds});
    }, [references, selectedLicencaWithPlan]);

    const availableResourceIds = useMemo(() => new Set(
        recursosDisponiveis
            .flatMap((modulo) => modulo.funcionalidades)
            .flatMap((funcionalidade) => funcionalidade.recursos)
            .map((recurso) => recurso.id)
            .filter(Boolean)
    ), [recursosDisponiveis]);

    const metrics = useMemo(() => ({
        total: rowsWithReferences.length,
        revisaoPendente: rowsWithReferences.filter((item) => !item.ultima_revisao).length,
        usuariosCobertos: rowsWithReferences.reduce((acc, item) => acc + Number(item.usuarios_vinculados || 0), 0),
    }), [rowsWithReferences]);

    const openCreate = useCallback(() => {
        setEditing(null);
        setForm({...createEmptyForm(), id_licenca: selectedLicencaId || '', id_sistema: selectedLicenca?.id_sistema || ''});
        setDialogOpen(true);
    }, [selectedLicenca, selectedLicencaId]);

    const openEdit = useCallback((row) => {
        setEditing(row);
        setForm(normalizeToForm(row));
        setDialogOpen(true);
    }, []);

    const openView = useCallback((row) => {
        setEditing(row);
        setForm({...normalizeToForm(row), viewMode: true});
        setDialogOpen(true);
    }, []);

    const submit = useCallback(async (overrideForm) => {
        const currentForm = overrideForm || form;
        if (!currentForm.id_licenca || !(currentForm.roleName?.trim() || currentForm.nome?.trim())) {
            setFeedback({error: 'Licença e nome da role são obrigatórios.', success: ''});
            return;
        }

        const currentFormWithAvailablePermissions = {
            ...currentForm,
            permissions: (currentForm.permissions || []).filter((item) => availableResourceIds.has(item?.resourceId || item?.resource?.id || '')),
        };
        const permissionsPayload = toPayload(currentFormWithAvailablePermissions).permissions;
        if (!permissionsPayload.length) {
            setFeedback({error: 'Selecione pelo menos uma permissão para a role.', success: ''});
            return;
        }

        setSaving(true);
        try {
            const payload = toPayload(currentFormWithAvailablePermissions);
            if (editing?.id) {
                await updatePermission(editing.id, payload);
                setFeedback({error: '', success: 'Role atualizada com sucesso.'});
            } else {
                await createPermission(payload);
                setFeedback({error: '', success: 'Role criada com sucesso.'});
            }
            setDialogOpen(false);
            await loadPermissions();
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível salvar a role.'), success: ''});
        } finally {
            setSaving(false);
        }
    }, [availableResourceIds, editing?.id, form, loadPermissions]);

    const confirmDeleteTarget = useCallback(async () => {
        if (!confirmDelete?.id) return;
        try {
            await deletePermission(confirmDelete.id);
            setConfirmDelete(null);
            setFeedback({error: '', success: 'Role excluída com sucesso.'});
            await loadPermissions();
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível excluir a role.'), success: ''});
        }
    }, [confirmDelete, loadPermissions]);

    const duplicateRole = useCallback(async (row) => {
        try {
            const duplicatePayload = toPayload({
                ...normalizeToForm(row),
                nome: `${row.roleName || row.nome} (cópia)`,
                roleName: `${row.roleName || row.nome} (cópia)`,
                descricao: row.description || row.descricao || '',
                description: row.description || row.descricao || '',
                permissions: normalizePermissions(row).filter((item) => availableResourceIds.has(item?.resourceId || item?.resource?.id || '')),
            });
            await createPermission(duplicatePayload);
            setFeedback({error: '', success: 'Role duplicada com sucesso.'});
            await loadPermissions();
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível duplicar a role.'), success: ''});
        }
    }, [availableResourceIds, loadPermissions]);

    return {
        rows: rowsWithReferences,
        metrics,
        loading,
        feedback,
        filter,
        dialogOpen,
        detailsTarget,
        editing,
        saving,
        confirmDelete,
        form,
        references,
        recursosDisponiveis,
        selectedLicencaId,
        selectedLicenca: selectedLicencaWithPlan,
        setFeedback,
        setFilter,
        setSelectedLicencaId,
        setDialogOpen,
        setDetailsTarget,
        setConfirmDelete,
        setForm,
        openCreate,
        openEdit,
        openView,
        submit,
        duplicateRole,
        confirmDeleteTarget,
        reload: loadPermissions,
    };
};
